import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowLeft, TrendingUp, Grid3x3, Sparkles, ExternalLink, Clock } from "lucide-react";
import api from "@/lib/api";
import IntegrationLogo from "@/components/IntegrationLogo";

/**
 * Sprint H — Public /integrations catalog page.
 *
 * ~600 integrations across ~25 categories. Search + category filter chip row
 * + Popular tab. Every card renders IntegrationLogo (logo.dev with monochrome
 * glyph fallback).
 */
export default function IntegrationsPublic() {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [popular, setPopular] = useState([]);
    const [q, setQ] = useState("");
    const [category, setCategory] = useState("all");
    const [tab, setTab] = useState("all"); // all | popular
    const [activeDetail, setActiveDetail] = useState(null);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [c, cats, pop] = await Promise.all([
                    api.get("/integrations/catalog"),
                    api.get("/integrations/categories"),
                    api.get("/integrations/popular?limit=20"),
                ]);
                setItems(c.data.integrations || []);
                setCategories(cats.data.categories || []);
                setPopular(pop.data.items || []);
                setTotal(cats.data.total || (c.data.integrations || []).length);
            } catch (e) {
                setLoadError(
                    e?.response?.data?.detail
                    || "Couldn't load the integration catalog — please refresh."
                );
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const filtered = useMemo(() => {
        const base = tab === "popular" ? popular : items;
        const term = q.trim().toLowerCase();
        return base.filter((i) => {
            if (category !== "all" && i.category !== category) return false;
            if (!term) return true;
            return (
                i.name?.toLowerCase().includes(term)
                || i.description?.toLowerCase().includes(term)
                || i.category_label?.toLowerCase().includes(term)
            );
        });
    }, [items, popular, category, q, tab]);

    return (
        <div className="min-h-screen bg-[#fafaf7] text-gray-900" data-testid="integrations-public">
            <header className="border-b border-[#e8e8e3] bg-white sticky top-0 z-40">
                <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
                    >
                        <ArrowLeft className="w-4 h-4" strokeWidth={2} /> Nexus One
                    </Link>
                    <div className="flex items-center gap-3 text-[13px]">
                        <Link to="/pricing" className="text-gray-500 hover:text-gray-900">Pricing</Link>
                        <Link to="/login" className="text-gray-500 hover:text-gray-900">Sign in</Link>
                        <Link
                            to="/signup"
                            className="inline-flex items-center gap-1 rounded-lg bg-[#16a34a] px-3 h-8 text-xs font-medium text-white hover:bg-[#15803d]"
                        >
                            Start free
                        </Link>
                    </div>
                </div>
            </header>

            <section className="border-b border-[#e8e8e3] bg-white">
                <div className="max-w-6xl mx-auto px-6 py-14 text-center">
                    <div className="wfos-label text-gray-500">Integration catalog</div>
                    <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-gray-900">
                        Every tool. One AI workforce.
                    </h1>
                    <p className="mt-3 text-base text-gray-600 max-w-2xl mx-auto">
                        <span className="font-bold text-gray-900">600+</span> integrations across CRM, ERP, comms, docs, dev, marketing, finance, HR, legal, and more. Your Workforce Members show up inside the tools your team already uses.
                    </p>
                    <div className="mt-8 max-w-xl mx-auto relative">
                        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={2} />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder={`Search 600+ tools…`}
                            data-testid="integrations-search-input"
                            className="w-full h-11 rounded-full border border-[#e8e8e3] bg-white pl-9 pr-4 text-sm focus:outline-none focus:border-[#16a34a] focus:ring-2 focus:ring-[#16a34a]/20"
                        />
                    </div>
                </div>
            </section>

            <section className="max-w-6xl mx-auto px-6 pt-6">
                {/* Tab bar */}
                <div className="flex items-center gap-1 border-b border-[#e8e8e3] mb-4">
                    <TabButton active={tab === "all"} onClick={() => setTab("all")} icon={<Grid3x3 className="w-3.5 h-3.5" strokeWidth={2} />} label={`All (${total})`} testid="integrations-tab-all" />
                    <TabButton active={tab === "popular"} onClick={() => setTab("popular")} icon={<TrendingUp className="w-3.5 h-3.5" strokeWidth={2} />} label={`Popular (${popular.length})`} testid="integrations-tab-popular" />
                </div>

                {/* Category chip strip */}
                <div className="flex gap-2 overflow-x-auto wfos-scrollbar pb-3 -mx-1 px-1" data-testid="category-chip-strip">
                    <CategoryChip active={category === "all"} onClick={() => setCategory("all")} label="All" count={total} testid="cat-chip-all" />
                    {categories.map((c) => (
                        <CategoryChip
                            key={c.key}
                            active={category === c.key}
                            onClick={() => setCategory(c.key)}
                            label={c.label}
                            count={c.count}
                            testid={`cat-chip-${c.key}`}
                        />
                    ))}
                </div>
            </section>

            <section className="max-w-6xl mx-auto px-6 pb-16">
                {loading ? (
                    <div className="py-16 text-center text-sm text-gray-400">Loading integrations…</div>
                ) : loadError ? (
                    <div className="py-16 text-center" data-testid="integrations-load-error">
                        <div className="text-sm text-red-700 font-medium">{loadError}</div>
                        <button
                            onClick={() => window.location.reload()}
                            className="mt-3 inline-flex items-center gap-1 rounded-md border border-[#e8e8e3] px-3 h-8 text-xs font-medium text-gray-700 hover:border-[#d6d6cf]"
                        >
                            Refresh
                        </button>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="py-16 text-center" data-testid="integrations-empty">
                        <div className="text-sm text-gray-500">No integrations match your search.</div>
                        <div className="mt-1 text-xs text-gray-400">Try a different keyword or browse categories.</div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="integrations-grid">
                        {filtered.map((i) => (
                            <IntegrationCard key={i.slug} integration={i} onClick={() => setActiveDetail(i)} />
                        ))}
                    </div>
                )}
                <div className="mt-8 text-center text-[11.5px] text-gray-400">
                    Showing {filtered.length} of {total} integrations. Missing one? Every hire request goes to the roadmap.
                </div>
            </section>

            {activeDetail && (
                <IntegrationDetailModal integration={activeDetail} onClose={() => setActiveDetail(null)} />
            )}
        </div>
    );
}

function TabButton({ active, onClick, icon, label, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-1.5 px-3 h-9 text-xs font-medium border-b-2 transition-colors ${
                active
                    ? "border-[#16a34a] text-gray-900"
                    : "border-transparent text-gray-500 hover:text-gray-900"
            }`}
        >
            {icon} {label}
        </button>
    );
}

function CategoryChip({ active, onClick, label, count, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-1 rounded-full px-3 h-8 text-[12px] font-medium whitespace-nowrap flex-shrink-0 border transition-colors ${
                active
                    ? "bg-[#16a34a] text-white border-[#16a34a]"
                    : "bg-white text-gray-700 border-[#e8e8e3] hover:border-[#d6d6cf]"
            }`}
        >
            {label} <span className={`text-[10px] tabular-nums ${active ? "opacity-80" : "text-gray-400"}`}>{count}</span>
        </button>
    );
}

export function IntegrationCard({ integration, onClick }) {
    const isMocked = integration.is_mocked ?? !integration.real_oauth_supported;
    return (
        <button
            onClick={onClick}
            data-testid={`integration-card-${integration.slug}`}
            className="wfos-card p-4 flex flex-col items-start gap-2 hover:border-[#d6d6cf] hover:shadow-sm transition-all text-left relative"
        >
            {isMocked && (
                <span
                    data-testid={`integration-coming-soon-${integration.slug}`}
                    className="absolute top-2 right-2 inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                    style={{
                        background: "var(--atlas-primary-muted)",
                        color: "var(--atlas-primary-hover)",
                        border: "1px solid rgba(139,92,246,0.45)",
                    }}
                >
                    <Clock className="w-2.5 h-2.5" strokeWidth={2} /> Coming Soon
                </span>
            )}
            <div className="flex items-start justify-between w-full">
                <IntegrationLogo integration={integration} size={40} rounded="rounded-lg" />
                {integration.real_oauth_supported && (
                    <span className="wfos-pill wfos-pill--green text-[8px]">Real OAuth</span>
                )}
            </div>
            <div className="text-[13.5px] font-semibold text-gray-900 truncate w-full">{integration.name}</div>
            <div className="text-[10.5px] text-gray-500 truncate w-full">{integration.category_label}</div>
            {(integration.recommend_count > 0) && (
                <div className="text-[10px] text-[#16a34a] font-medium">
                    Recommended for {integration.recommend_count} {integration.recommend_count === 1 ? "worker" : "workers"}
                </div>
            )}
        </button>
    );
}

function IntegrationDetailModal({ integration, onClose }) {
    return (
        <div
            className="fixed inset-0 bg-black/40 grid place-items-center z-[100] p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            data-testid="integration-detail-modal"
        >
            <div className="wfos-card w-full max-w-[540px] max-h-[85vh] overflow-y-auto p-6">
                <div className="flex items-start gap-4">
                    <IntegrationLogo integration={integration} size={56} rounded="rounded-xl" />
                    <div className="flex-1 min-w-0">
                        <div className="text-xl font-bold text-gray-900">{integration.name}</div>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                            <span className="wfos-pill text-[10px]">{integration.category_label}</span>
                            {integration.real_oauth_supported && <span className="wfos-pill wfos-pill--green text-[10px]">Real OAuth</span>}
                            {(integration.is_mocked ?? !integration.real_oauth_supported) && (
                                <span
                                    className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                                    style={{
                                        background: "var(--atlas-primary-muted)",
                                        color: "var(--atlas-primary-hover)",
                                        border: "1px solid rgba(139,92,246,0.45)",
                                    }}
                                    data-testid={`integration-detail-coming-soon-${integration.slug}`}
                                >
                                    <Clock className="w-2.5 h-2.5" strokeWidth={2} /> Coming Soon
                                </span>
                            )}
                            {integration.domain && (
                                <a
                                    href={`https://${integration.domain}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-[#16a34a]"
                                >
                                    {integration.domain} <ExternalLink className="w-3 h-3" strokeWidth={2} />
                                </a>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-800 text-xl leading-none">×</button>
                </div>
                <p className="mt-4 text-sm text-gray-700 leading-relaxed">{integration.description}</p>

                {integration.ai_relevant_roles?.length > 0 && (
                    <div className="mt-5">
                        <div className="wfos-label text-gray-500 mb-2">Recommended for</div>
                        <div className="flex flex-wrap gap-1.5" data-testid="integration-detail-roles">
                            {integration.ai_relevant_roles.map((r) => (
                                <span key={r} className="wfos-pill text-[10.5px] capitalize">{r.replace("-", " ")}</span>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mt-6 pt-4 border-t border-[#e8e8e3] flex items-center justify-between">
                    <div className="text-[11px] text-gray-500 inline-flex items-center gap-1">
                        <Sparkles className="w-3 h-3 text-[#16a34a]" strokeWidth={2} />
                        Available on every hire — install from any worker's Connectors panel.
                    </div>
                    <Link to="/signup" className="rounded-lg bg-[#16a34a] px-3 h-9 text-xs font-semibold text-white hover:bg-[#15803d] inline-flex items-center gap-1">
                        Start free
                    </Link>
                </div>
            </div>
        </div>
    );
}
